import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, Output, inject } from '@angular/core';
import { AnimationItem } from 'lottie-web';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-flyout-animation',
  imports: [LottieComponent],
  templateUrl: './flyout-animation.component.html',
  styleUrls: ['./flyout-animation.component.scss'],

  standalone: true,
})
export class FlyoutAnimationComponent implements OnDestroy {
  private http = inject(HttpClient);
  private destroy$ = new Subject<void>();
  private loadToken = 0;
  private animToken = 0;
  private destroyed = false;
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  @Input() set lottiePath(value: string) {
    if (value) {
      this._lottiePath = value;
      void this.loadLottie(value);
    }
  }
  get lottiePath() {
    return this._lottiePath;
  }
  private _lottiePath = '';

  /** @deprecated filename-only paths under old lotti_animation folder */
  @Input() set lottieUrl(value: string) {
    if (value) {
      this._lottieUrl = value;
      this.lottiePath = this.legacyAssetPath(value);
    }
  }
  get lottieUrl() { return this._lottieUrl; }
  private _lottieUrl = '';
  @Input() type: string;
  @Input() delta = 0;
  @Input() displayLabel = '';

  @Output() done = new EventEmitter<void>();

  visible = true;
  lottieOptions: AnimationOptions = { autoplay: false, loop: false };
  private animationItem!: AnimationItem;

  ngOnDestroy(): void {
    this.destroyed = true;
    this.loadToken++;
    this.animToken++;
    this.clearPendingTimeouts();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Prefer animationData (fetched JSON) over path XHR for remote URLs.
   * Same-origin `assets/` paths are passed through to ngx-lottie.
   */
  private async loadLottie(rawPath: string): Promise<void> {
    const token = ++this.loadToken;
    const path = this.normalizeLottiePath(rawPath);

    // Local assets: let ngx-lottie load via path (same origin).
    if (path.startsWith('assets/') || path.startsWith('/assets/')) {
      if (this.destroyed || token !== this.loadToken) return;
      this.lottieOptions = {
        ...this.lottieOptions,
        path,
        animationData: undefined,
      };
      return;
    }

    try {
      const animationData = await firstValueFrom(
        this.http.get(path).pipe(takeUntil(this.destroy$))
      );
      if (this.destroyed || token !== this.loadToken) return;
      this.lottieOptions = {
        ...this.lottieOptions,
        animationData,
        path: undefined,
      };
    } catch (err) {
      // Destroy/cancel yields EmptyError — do not apply fallback after teardown
      if (this.destroyed || token !== this.loadToken) return;
      this.lottieOptions = {
        ...this.lottieOptions,
        path,
        animationData: undefined,
      };
    }
  }

  /** Accept Firebase Storage https URLs, absolute paths, assets/, or legacy filenames. */
  private normalizeLottiePath(value: string): string {
    if (
      value.startsWith('http://') ||
      value.startsWith('https://') ||
      value.startsWith('assets/') ||
      value.startsWith('/')
    ) {
      return value;
    }
    return this.legacyAssetPath(value);
  }

  onLottieCreated(anim: AnimationItem) {
    const token = ++this.animToken;
    this.animationItem = anim;
    let finished = false;
    const finish = () => {
      if (finished || this.destroyed || token !== this.animToken) return;
      finished = true;
      this.done.emit();
    };

    // Keep HUD balance visible (previous value) through the fly-in;
    // profile tab swaps to the live amount when presentation is cleared after done.
    this.scheduleTimeout(() => {
      if (this.destroyed || token !== this.animToken) return;
      try {
        anim.play();
      } catch {
        finish();
      }
    }, 400);

    /* when Lottie completes, emit */
    const onComplete = () => {
      this.scheduleTimeout(() => finish(), 200);
    };
    const onFailed = () => {
      finish();
    };
    anim.addEventListener('complete', onComplete);
    anim.addEventListener('data_failed', onFailed);
    // Safety: remote Storage URLs can hang without complete/data_failed
    this.scheduleTimeout(() => finish(), 10000);
  }

  private scheduleTimeout(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      this.pendingTimeouts = this.pendingTimeouts.filter((t) => t !== id);
      fn();
    }, ms);
    this.pendingTimeouts.push(id);
  }

  private clearPendingTimeouts(): void {
    for (const id of this.pendingTimeouts) {
      clearTimeout(id);
    }
    this.pendingTimeouts = [];
  }

  private legacyAssetPath(file: string) {
    return `assets/lotti_animation/game_page/${file}`;
  }
}
